import React, { useState } from 'react';
import * as yup from 'yup';
import OForm from '../common/OForm';
import { medicalSpecialties } from '../../utils/constants/medicalSpecialties';
import { cleanTaxId, isValidTaxId, cleanNPI, isValidNPI } from '../../utils/helpers/format-phone-number';
import useProviderForm from '../../hooks/useProviderForm';
import FacilityDepartmentAssignment from './FacilityDepartmentAssignment';
import BulkUploadModal from './BulkUploadModal';
import BulkUploadValidationTable from './BulkUploadValidationTable';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useTheme } from '../../ThemeContext';
import { downloadProviderBulkTemplate, validateProviderBulkData } from '../../utils/apiClient';
import { message } from 'antd';

const validationSchema = yup.object().shape({
  fullName: yup.string().required('Full Name is required'),
  npi: yup
    .string()
    .required('NPI is required')
    .test('valid-npi', 'NPI must be exactly 10 digits', (value) => {
      return isValidNPI(value);
    }),
  taxId: yup
    .string()
    .required('Tax ID is required')
    .test('valid-tax-id', 'Tax ID must be exactly 9 digits', (value) => {
      return isValidTaxId(value);
    }),
  specialties: yup.array().of(yup.string()).min(1, 'At least one specialty is required'),
  affiliations: yup.array() // Basic array validation, detailed checks in handleSubmit
});

const ProviderForm = ({ 
  onSubmit, 
  handleBack, 
  setRenderType, 
  renderType, 
  initialValues, 
  isSubmitting, 
  provider, 
  onRefresh 
}) => {
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showValidationTable, setShowValidationTable] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const { themeMode } = useTheme();

  const {
    initialFormValues,
    handleFormSubmit
  } = useProviderForm(provider, onSubmit);

  // Custom onSubmit handler to clean numbers before sending to backend
  const handleSubmit = (values) => {
    // Validate affiliations manually and show toast messages
    const validAffiliations = (values.affiliations || []).filter(aff => aff.facilityId && aff.facilityId !== '');
    
    console.log('=== FORM SUBMISSION VALIDATION ===');
    console.log('All affiliations:', values.affiliations);
    console.log('Valid affiliations (with facilityId):', validAffiliations);
    
    if (validAffiliations.length === 0) {
      message.error('Please select at least one facility');
      return;
    }
    
    // Check each affiliation for roles
    const affiliationsWithoutRoles = [];
    validAffiliations.forEach((aff, index) => {
      const isFacilityLevel = !aff.departmentId || aff.departmentId === '';
      console.log(`Affiliation ${index + 1}:`, {
        facilityId: aff.facilityId,
        departmentId: aff.departmentId,
        isFacilityLevel: isFacilityLevel,
        roles: aff.role,
        roleCount: aff.role?.length || 0
      });
      
      if (!aff.role || aff.role.length === 0) {
        console.log(`  ⚠️ Affiliation ${index + 1} has no roles!`);
        affiliationsWithoutRoles.push(aff);
      }
    });
    
    if (affiliationsWithoutRoles.length > 0) {
      console.log('⚠️ Affiliations missing roles:', affiliationsWithoutRoles);
      message.error('Each selected department must have at least one role assigned. Please add roles to all selected departments.');
      return;
    }
    
    console.log('✅ All validations passed. Proceeding with submission...');
    
    const cleanedValues = {
      ...values,
      npi: cleanNPI(values.npi),
      taxId: cleanTaxId(values.taxId)
    };
    handleFormSubmit(cleanedValues);
  };

  // Handler for Upload in Bulk button
  const handleUploadInBulk = () => {
    setShowBulkUploadModal(true);
  };

  // Handler for file upload from modal
  const handleFileUpload = async (file) => {
    try {
      // Close the upload modal
      setShowBulkUploadModal(false);
      
      // Show loading message
      message.loading('Validating provider data...', 0);
      
      // Call API to validate the uploaded file
      const response = await validateProviderBulkData(file);
      
      // Hide loading message
      message.destroy();
      
      if (response.statusCode === 'SUCCESS') {
        // Store validation data and show validation table
        setValidationData(response);
        setShowValidationTable(true);
        
        const summary = response.response.summary;
        if (summary.invalidRows > 0) {
          message.warning(`Validation complete: ${summary.validRows} valid, ${summary.invalidRows} invalid rows. Please fix errors before submitting.`);
        } else {
          message.success(`Validation complete: All ${summary.validRows} rows are valid!`);
        }
      } else {
        message.error('Validation failed. Please check your file and try again.');
      }
    } catch (error) {
      console.error('Error validating file:', error);
      message.destroy();
      message.error('Failed to validate file. Please try again.');
    }
  };

  // Handler for going back from validation table
  const handleBackFromValidation = () => {
    setShowValidationTable(false);
    setValidationData(null);
  };

  // Handler for successful provider creation
  const handleBulkUploadSuccess = async (response) => {
    setShowValidationTable(false);
    setValidationData(null);
    
    // Refresh the provider list to show newly created providers
    if (onRefresh) {
      try {
        await onRefresh();
      } catch (error) {
        console.error('Error refreshing provider list:', error);
      }
    }
    
    // Navigate back to list view
    if (setRenderType) {
      setRenderType('list');
    }
  };

  // Handler for Download Template button
  const handleDownloadTemplate = async () => {
    try {
      // Show loading message
      message.loading('Downloading template...', 0);
      
      // Call API to download the template
      const { blob, filename } = await downloadProviderBulkTemplate();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Hide loading and show success message
      message.destroy();
      message.success('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      message.destroy();
      message.error('Failed to download template. Please try again.');
    }
  };

  const getFields = (watch, { setValue, getValues }) => {
    const baseFields = [
      // Basic Information Section
      { 
        fieldName: 'fullName', 
        renderType: 'text', 
        label: 'Full Name*', 
        isRequired: true, 
        section: 'Basic Information',
      },
      { 
        fieldName: 'npi', 
        renderType: 'text', 
        label: 'NPI Number*', 
        isRequired: true, 
        section: 'Basic Information',
      },
      { 
        fieldName: 'taxId', 
        renderType: 'text', 
        label: 'Tax ID*', 
        isRequired: true, 
        section: 'Basic Information',
      },
      // Specialties Section
      { 
        fieldName: 'specialties', 
        renderType: 'select',
        type: 'multi-select',
        label: 'Specialties*', 
        isRequired: true, 
        section: 'Basic Information',
        options: medicalSpecialties,
        valueKey: 'value',
        labelKey: 'label',
        isCreatable: true,
      },
      // Facility & Department Assignment Section
      {
        fieldName: 'affiliations',
        renderType: 'custom',
        customComponent: FacilityDepartmentAssignment,
        label: 'Facility & Department Assignment*',
        isRequired: true,
        section: 'Facility & Department Assignment',
        sectionInfo: 'Select facilities from the left panel, then assign departments and roles from the right panel.',
        gridColumns: 12
      }
    ];

    return baseFields;
  };

  // Create Download Template button for header
  const downloadButton = (
    <button
      onClick={handleDownloadTemplate}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: themeMode === 'dark' ? 'transparent' : '#ffffff',
        color: themeMode === 'dark' ? '#FF6B47' : '#000000',
        border: '1px solid #FF6B47',
        borderRadius: '6px',
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        boxShadow: 'none',
        outline: 'none'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = themeMode === 'dark' ? 'rgba(255, 107, 71, 0.1)' : '#fff5f3';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = themeMode === 'dark' ? 'transparent' : '#ffffff';
      }}
    >
      <FileDownloadOutlinedIcon style={{ fontSize: '18px', color: '#FF6B47' }} />
      Download Template
    </button>
  );

  // Create Upload in Bulk button for header
  const uploadButton = (
    <button
      onClick={handleUploadInBulk}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: '#FF6B47',
        color: '#ffffff',
        border: 'none',
        borderRadius: '6px',
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        boxShadow: 'none',
        outline: 'none'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FF5533'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B47'}
    >
      <FileUploadOutlinedIcon style={{ fontSize: '18px' }} />
      Upload in bulk
    </button>
  );

  // If validation table is shown, render it instead of the form
  if (showValidationTable && validationData) {
    return (
      <BulkUploadValidationTable
        validationData={validationData}
        onBack={handleBackFromValidation}
        onSuccess={handleBulkUploadSuccess}
      />
    );
  }

  return (
    <>
      <OForm
        formTitle={null}
        initialValues={initialFormValues}
        fields={getFields}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        handleBack={handleBack}
        fieldsPerRow={4}
        submitButtonText={isSubmitting ? "Submitting..." : "Submit"}
        isSubmitting={isSubmitting}
        headerButtonLabel="Back"
        headerButtonOnClick={() => setRenderType(renderType === 'list' ? 'form' : 'list')}
        scrollableSections={[]}
        additionalHeaderButtons={[downloadButton, uploadButton]}
      />
      
      {/* Bulk Upload Modal */}
      <BulkUploadModal
        open={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onUpload={handleFileUpload}
      />
    </>
  );
};

export default ProviderForm;
